import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Globe, Send, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { SITE, IMAGES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      setForm({ name: "", email: "", phone: "", message: "" });
      toast.success("Thank you — your message has been received. I'll be in touch soon.");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const contactItems = [
    { icon: Phone, label: "Phone", value: SITE.phone },
    { icon: Mail, label: "Email", value: SITE.email },
    { icon: MapPin, label: "Location", value: SITE.location },
    { icon: Globe, label: "Website", value: SITE.website },
  ];

  return (
    <div data-testid="contact-page">
      {/* Header */}
      <section className="pt-16 pb-10 lg:pt-24 lg:pb-14 bg-white border-b border-[#6B8F71]/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#6B8F71] mb-4">Contact Us</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#355C4A]">
            Let's Begin Your Journey
          </h1>
          <p className="mt-5 text-lg font-light text-[#5A655F] max-w-2xl mx-auto">
            Taking the first step can feel difficult — but it can also be the beginning of meaningful change.
            If you're ready to begin, I'd be honoured to walk alongside you.
          </p>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
          {/* Info + image */}
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={fade}>
            <div className="overflow-hidden rounded-[2rem] shadow-[0_8px_40px_rgba(107,143,113,0.15)] mb-8">
              <img src={IMAGES.room} alt="Calm space" className="w-full h-64 object-cover" />
            </div>
            <div className="space-y-4">
              {contactItems.map((c, i) => (
                <div
                  key={i}
                  data-testid={`contact-info-${c.label.toLowerCase()}`}
                  className="flex items-center gap-4 bg-white rounded-2xl p-5 border border-[#6B8F71]/10 shadow-[0_8px_30px_rgba(107,143,113,0.05)]"
                >
                  <span className="flex items-center justify-center w-12 h-12 rounded-full bg-[#6B8F71]/12 shrink-0">
                    <c.icon strokeWidth={1.5} className="w-5 h-5 text-[#355C4A]" />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] text-[#6B8F71]">{c.label}</p>
                    <p className="text-[#2F2F2F] font-light">{c.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Form */}
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={fade}>
            <div className="bg-white rounded-[2rem] p-8 lg:p-10 border border-[#6B8F71]/10 shadow-[0_8px_40px_rgba(107,143,113,0.1)]">
              {done ? (
                <div data-testid="contact-success" className="text-center py-10">
                  <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#6B8F71]/15 mb-6">
                    <Check strokeWidth={2} className="w-8 h-8 text-[#355C4A]" />
                  </span>
                  <h3 className="font-serif text-3xl text-[#355C4A]">Message received</h3>
                  <p className="mt-3 text-[#5A655F] font-light">
                    Thank you for reaching out. I'll get back to you as soon as possible.
                  </p>
                  <button
                    data-testid="contact-send-another"
                    onClick={() => setDone(false)}
                    className="mt-8 border border-[#6B8F71] text-[#355C4A] px-6 py-3 rounded-full hover:bg-[#6B8F71]/10 transition-all font-medium"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                  <h3 className="font-serif text-3xl text-[#355C4A] mb-1">Book Your Appointment</h3>
                  <p className="text-[#5A655F] font-light mb-4">No pressure. No judgment. Just a first conversation.</p>

                  <div>
                    <Label htmlFor="name" className="text-[#355C4A] mb-1.5 block">Your Name *</Label>
                    <Input
                      id="name"
                      data-testid="contact-name-input"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Full name"
                      className="bg-[#FAF8F4] border-[#6B8F71]/30 rounded-xl h-12 focus-visible:ring-[#6B8F71]"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="email" className="text-[#355C4A] mb-1.5 block">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="contact-email-input"
                        value={form.email}
                        onChange={update("email")}
                        placeholder="you@email.com"
                        className="bg-[#FAF8F4] border-[#6B8F71]/30 rounded-xl h-12 focus-visible:ring-[#6B8F71]"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-[#355C4A] mb-1.5 block">Phone</Label>
                      <Input
                        id="phone"
                        data-testid="contact-phone-input"
                        value={form.phone}
                        onChange={update("phone")}
                        placeholder="Optional"
                        className="bg-[#FAF8F4] border-[#6B8F71]/30 rounded-xl h-12 focus-visible:ring-[#6B8F71]"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-[#355C4A] mb-1.5 block">How can I help? *</Label>
                    <Textarea
                      id="message"
                      data-testid="contact-message-input"
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Share a little about what you'd like support with..."
                      rows={5}
                      className="bg-[#FAF8F4] border-[#6B8F71]/30 rounded-xl focus-visible:ring-[#6B8F71] resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="contact-submit-btn"
                    disabled={loading}
                    className="w-full bg-[#6B8F71] text-white px-8 py-4 rounded-full hover:bg-[#355C4A] hover:-translate-y-0.5 transition-all duration-300 font-medium inline-flex items-center justify-center gap-2 disabled:opacity-70 disabled:hover:translate-y-0"
                  >
                    {loading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>
                    ) : (
                      <>Send Message <Send strokeWidth={1.75} className="w-4 h-4" /></>
                    )}
                  </button>
                  <p className="text-xs text-center text-[#5A655F] font-light">
                    Your message is confidential and treated with care.
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
